import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { PortalLayout } from "@/components/layout/PortalLayout";
import { GlassCard } from "@/components/GlassCard";
import { StatusBadge } from "@/components/Badges";
import api from "@/lib/api";
import { goldBtnSm, glassBtn } from "@/lib/constants";

export default function CrossReview() {
  const [deals, setDeals] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [selDeal, setSelDeal] = useState("");
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const [d, r] = await Promise.all([api.get("/admin/deals"), api.get("/admin/cross-review")]);
    setDeals(d.data); setReviews(r.data);
    if (d.data[0]) setSelDeal((prev) => prev || d.data[0].id);
  }, []);
  useEffect(() => { load(); }, [load]);

  const run = async () => {
    if (!selDeal) return;
    setBusy(true);
    try { const { data } = await api.post("/admin/cross-review", { deal_id: selDeal }); toast.success("Cross-review complete."); await load(); openReview(data.id); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const openReview = async (id) => { const { data } = await api.get(`/admin/cross-review/${id}`); setDetail(data); };
  const decide = async (pid, decision) => {
    await api.post(`/admin/cross-review/proposals/${pid}/decision`, { decision });
    toast.success(`Proposal ${decision}d.`);
    if (detail) openReview(detail.review.id);
  };

  return (
    <PortalLayout title="Cross-Review">
      <p className="text-sm text-muted-foreground mb-6">Trigger ORLA to review a deal, surface findings, and generate proposals for your approval.</p>
      <GlassCard className="p-6 mb-6">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="pdg-kicker block mb-2">Deal</label>
            <select value={selDeal} onChange={(e) => setSelDeal(e.target.value)} data-testid="cross-review-deal-select"
              className="h-10 rounded-lg bg-[hsl(var(--pdg-charcoal))] border border-border text-sm px-3 text-foreground min-w-[260px]">
              {deals.map((d) => <option key={d.id} value={d.id}>{d.title}</option>)}
            </select>
          </div>
          <button onClick={run} disabled={busy} data-testid="cross-review-run-button" className={goldBtnSm + " h-10"}>{busy ? "Reviewing…" : "Run cross-review"}</button>
        </div>
      </GlassCard>

      <div className="grid gap-6 lg:grid-cols-2">
        <GlassCard className="p-6">
          <h2 className="pdg-serif text-2xl text-foreground mb-4">Reviews</h2>
          <div className="space-y-2">
            {reviews.map((r) => (
              <button key={r.id} onClick={() => openReview(r.id)} className="w-full text-left p-3 rounded-lg border border-border hover:border-[hsl(var(--pdg-gold)/0.35)] transition-colors">
                <div className="flex items-center justify-between"><span className="text-sm text-foreground">{r.deal_title}</span><StatusBadge status={r.status} /></div>
                <div className="text-xs text-muted-foreground mt-1">{new Date(r.created_at).toLocaleString()}</div>
              </button>
            ))}
            {reviews.length === 0 && <p className="text-sm text-muted-foreground">No reviews yet.</p>}
          </div>
        </GlassCard>

        <GlassCard className="p-6">
          <h2 className="pdg-serif text-2xl text-foreground mb-4">Findings &amp; proposals</h2>
          {!detail ? <p className="text-sm text-muted-foreground">Select a review to view findings.</p> : (
            <div className="space-y-5">
              <div>
                <div className="pdg-kicker mb-2">Findings</div>
                <div className="space-y-2">
                  {detail.findings.map((f) => (
                    <div key={f.id} className="p-3 rounded-lg border border-border">
                      <div className="flex items-center justify-between"><span className="text-sm text-foreground">{f.category}</span><StatusBadge status={f.severity === "high" ? "error" : f.severity === "medium" ? "attention" : "queued"} label={f.severity} /></div>
                      <p className="text-xs text-muted-foreground mt-1">{f.detail}</p>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <div className="pdg-kicker mb-2">Proposals</div>
                <div className="space-y-2">
                  {detail.proposals.map((p) => (
                    <div key={p.id} className="p-3 rounded-lg border border-border">
                      <div className="flex items-center justify-between gap-3">
                        <div><div className="text-sm text-foreground">{p.action}</div><p className="text-xs text-muted-foreground mt-1">{p.detail}</p></div>
                        <StatusBadge status={p.decision === "approved" ? "verified" : p.decision === "rejected" ? "declined" : "pending"} label={p.decision} />
                      </div>
                      {p.decision === "pending" && (
                        <div className="mt-3 flex gap-2">
                          <button onClick={() => decide(p.id, "approve")} data-testid={`proposal-approve-${p.id}`} className={goldBtnSm}>Approve</button>
                          <button onClick={() => decide(p.id, "reject")} className={glassBtn + " h-9 px-3 text-xs"}>Reject</button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </GlassCard>
      </div>
    </PortalLayout>
  );
}
